import {
  normalizeSubscriptionDurationLabel,
  subscriptionDurationLabel,
} from '../pages/receptionist/sales/saleDurationUtils';

/** Sale / payment day (paid_at) — not membership gym start. */
export function membershipEntryDateDisplay(m) {
  if (!m) return '—';
  const paid = m?.paymentDate || m?.paid_at || m?.paidAt;
  if (paid && String(paid).trim() && String(paid).slice(0, 10) !== '—') {
    return String(paid).slice(0, 10);
  }
  const joining = m?.joiningDate || m?.memberJoiningDate;
  if (joining && String(joining).trim()) return String(joining).slice(0, 10);
  const created = m?.createdAt || m?.date;
  if (created && String(created).trim()) return String(created).slice(0, 10);
  return '—';
}

/** Entry Date column for membership tables / exports (payment day). */
export function membershipDateDisplay(m) {
  return membershipEntryDateDisplay(m);
}

/** User payment note from subscription / linked payment (hides system Duration: segments). */
export function membershipPaymentNoteDisplay(m) {
  const raw = String(m?.paymentNote || m?.payment_note || '').trim();
  if (!raw) return '—';
  const cleaned = raw
    .split(' · ')
    .map((part) => part.trim())
    .filter((part) => part && !/^(Duration:|Renewed|Complimentary renewal|Phone:)/i.test(part))
    .join(' · ')
    .trim();
  return cleaned || '—';
}

/** Sort memberships oldest → newest by the Date column (for custom date filters). */
export function sortMembershipsByDateAsc(rows) {
  return [...(rows || [])].sort((a, b) => {
    const da = membershipDateDisplay(a);
    const db = membershipDateDisplay(b);
    const sa = (!da || da === '—') ? '9999-12-31' : String(da).slice(0, 10);
    const sb = (!db || db === '—') ? '9999-12-31' : String(db).slice(0, 10);
    const byDate = sa.localeCompare(sb);
    if (byDate !== 0) return byDate;
    return String(a.id || '').localeCompare(String(b.id || ''));
  });
}

/** Membership package start_date (gym access start) — not Entry Date / payment day. */
export function membershipStartDisplay(m) {
  if (m?.datesTbc && !(m.start || m.startDate)) return 'TBC';
  const value = m?.start || m?.startDate;
  return value && String(value).trim() ? value : '—';
}

/** Display expiry / end date for membership tables / exports. */
export function membershipExpiryDisplay(m) {
  if (m?.datesTbc && !(m.expiry || m.expiryDate)) return 'TBC';
  const value = m?.expiry || m?.expiryDate;
  return value && String(value).trim() ? value : '—';
}

/**
 * Package duration shown on membership tables.
 * Prefer duration saved at sale, then start→expiry, then package catalog.
 * Matches Subscription options ("1 month", …) or "TBC".
 */
export function membershipDurationDisplay(m, packageDurationByName) {
  if (!m) return '—';
  if (m.lifetime) return 'Lifetime';
  if (m.datesTbc) return 'TBC';

  // Duration chosen when the subscription was saved
  const sold = normalizeSubscriptionDurationLabel(m.duration || m.packageDuration || '');
  if (sold && sold !== 'TBC') return sold;

  const start = String(m.start || m.startDate || '').slice(0, 10);
  const end = String(m.expiry || m.expiryDate || '').slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(start) && /^\d{4}-\d{2}-\d{2}$/.test(end)) {
    const startDate = new Date(`${start}T12:00:00`);
    const endDate = new Date(`${end}T12:00:00`);
    if (!Number.isNaN(startDate.getTime()) && !Number.isNaN(endDate.getTime()) && endDate >= startDate) {
      let months = (endDate.getFullYear() - startDate.getFullYear()) * 12
        + (endDate.getMonth() - startDate.getMonth());
      if (endDate.getDate() < startDate.getDate()) months -= 1;
      // Period ends day-before next cycle (e.g. 1 month ≈ 28–31 days) — round up near month boundary
      if (months < 1) {
        const days = Math.round((endDate - startDate) / 86400000);
        if (days >= 25) months = 1;
      }
      if (months >= 1) {
        return subscriptionDurationLabel(months) || (months === 1 ? '1 month' : `${months} months`);
      }
      const days = Math.round((endDate - startDate) / 86400000);
      if (days > 0) return days === 1 ? '1 day' : `${days} days`;
    }
  }

  const catalog = packageDurationByName instanceof Map
    ? packageDurationByName.get(String(m.package || '').trim().toLowerCase())
    : '';
  const fromCatalog = normalizeSubscriptionDurationLabel(catalog);
  if (fromCatalog) return fromCatalog;

  return '—';
}
